import React from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { useChatStore } from "../../../stores/chatStore";
import { ContentCard } from "../content-card/content-card";
import { crashCourseTitleStyles } from "./styles";

const getFirstNonEmptyLine = (text?: string): string => {
  if (!text) return "";
  const lines = text.split(/\r?\n/).map((line) => line.trim());
  return lines.find((line) => line.length > 0) || "";
};

const stripMarkdownHeading = (s: string): string => s.replace(/^#{1,6}\s+/, "").trim();
const stripMarkdownWrappingEmphasis = (s: string): string => {
  const t = s.trim();
  const m = t.match(/^(\*\*|__|\*|_)([\s\S]*?)\1$/);
  return (m?.[2] ?? t).trim();
};

interface CrashCoursesPanelProps {
  onMessageClick: (messageId: string, domain: string, focus: string) => void;
}

export const CrashCoursesPanel: React.FC<CrashCoursesPanelProps> = ({
  onMessageClick,
}) => {
  const { crashCourses, isLoadingCrashCourses, errorCrashCourses } =
    useChatStore();

  if (isLoadingCrashCourses) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={98}
        sx={{ borderRadius: 1 }}
      />
    );
  }
  if (errorCrashCourses) {
    return <Typography color="error">Error: {errorCrashCourses}</Typography>;
  }

  if (!crashCourses || crashCourses.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No Crash Courses yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: "300px" }}
        >
          Select the option "Create crash course" in the bottom left menu to
          generate the Crash Course
        </Typography>
      </Box>
    );
  }

  const sortedCrashCourses = [...crashCourses].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {sortedCrashCourses.map((crashCourse) => {
        const rawTitle =
          crashCourse.title?.trim() || getFirstNonEmptyLine(crashCourse.text);
        const title = stripMarkdownHeading(stripMarkdownWrappingEmphasis(rawTitle));
        const displayItem = title ? { ...crashCourse, text: "" } : crashCourse;

        return (
          <ContentCard
            key={crashCourse.message_id}
            item={displayItem}
            onClick={(id, domain, focus) =>
              onMessageClick(id, domain || "", focus || "")
            }
            badgeLabel="Crash Course"
            header={
              title ? (
                <Typography sx={crashCourseTitleStyles}>{title}</Typography>
              ) : null
            }
          />
        );
      })}
    </Box>
  );
};
